/*
 * Dockside wishlist: kept in the visitor's browser (localStorage), newest first, as a list of product handles.
 * - [data-dsi-wish-count]: shows how many items are saved.
 * - [data-dsi-wish-toggle][data-handle]: saves or removes a product. data-add and data-remove are its two labels.
 * - [data-dsi-wishlist]: the wishlist page. It holds a hidden card for every catalog product ([data-handle]) and shows
 *   the saved ones, in the order they were saved. data-complete means every product is there, so a saved handle with
 *   no card (a product removed from the catalog) can be dropped.
 * Where the browser won't store anything, the toggles stay hidden and the page says so.
 * Listeners sit on the document, so they keep working when the theme editor re-renders a section.
 */
(() => {
  if (window.dsiWishlistReady) return;
  window.dsiWishlistReady = true;

  const KEY = 'dsi-wishlist';

  const read = () => {
    try {
      const list = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string') : [];
    } catch (error) {
      return null;
    }
  };

  const write = (list) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
      return true;
    } catch (error) {
      return false;
    }
  };

  const render = () => {
    const list = read();
    const available = list !== null;
    const saved = list || [];

    document.querySelectorAll('[data-dsi-wish-count]').forEach((count) => {
      count.textContent = String(saved.length);
    });

    document.querySelectorAll('[data-dsi-wish-toggle]').forEach((toggle) => {
      const on = saved.includes(toggle.dataset.handle);
      toggle.hidden = !available;
      const label = on ? toggle.dataset.remove : toggle.dataset.add;
      if (label) toggle.textContent = label;
    });

    document.querySelectorAll('[data-dsi-wishlist]').forEach((page) => {
      const grid = page.querySelector('[data-dsi-wishlist-grid]');
      if (!grid) return;
      const items = new Map([...grid.querySelectorAll(':scope > [data-handle]')].map((item) => [item.dataset.handle, item]));

      if (available && page.hasAttribute('data-complete')) {
        const kept = saved.filter((handle) => items.has(handle));
        if (kept.length !== saved.length) {
          write(kept);
          saved.splice(0, saved.length, ...kept);
          document.querySelectorAll('[data-dsi-wish-count]').forEach((count) => {
            count.textContent = String(saved.length);
          });
        }
      }

      items.forEach((item) => {
        item.hidden = true;
      });
      saved.forEach((handle) => {
        const item = items.get(handle);
        if (!item) return;
        item.hidden = false;
        grid.appendChild(item);
      });

      const shown = saved.filter((handle) => items.has(handle)).length;
      const countLine = page.querySelector('[data-dsi-wishlist-count]');
      if (countLine) {
        countLine.hidden = shown === 0;
        countLine.textContent = `${shown} ${shown === 1 ? countLine.dataset.one : countLine.dataset.other}`;
      }
      grid.hidden = shown === 0;
      const empty = page.querySelector('[data-dsi-wishlist-empty]');
      if (empty) empty.hidden = !available || shown > 0;
      const unavailable = page.querySelector('[data-dsi-wishlist-unavailable]');
      if (unavailable) unavailable.hidden = available;
    });
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target instanceof Element ? event.target.closest('[data-dsi-wish-toggle]') : null;
    if (!toggle) return;
    const list = read();
    if (list === null) return;
    const handle = toggle.dataset.handle;
    const next = list.includes(handle) ? list.filter((item) => item !== handle) : [handle, ...list];
    if (write(next)) render();
  });

  // Another tab changed the list.
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) render();
  });

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', render);

  const page = document.querySelector('[data-dsi-wishlist]');
  if (page && page.dataset.title) document.title = page.dataset.title;

  render();
})();
